import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import "./ClientPlatformAnalytics.css";
import "./PublicPlatformReport.css";
import "./PublicPlatformConnect.css";
import CphereHeaderLogo from "../components/CphereHeaderLogo";
import CphereBrandFooter from "../components/CphereBrandFooter";
import PlatformHeroMark from "../components/PlatformHeroMark";
import { resolvePlatformHeroImage } from "../utils/platformBranding";
import AdvertiserIdentityMark from "../components/AdvertiserIdentityMark";
import {
  CONNECT_STATUS_POLL_INTERVAL_MS,
  connectActionFor,
  factRows,
  formatProgressLine,
  formatTimestamp,
  progressPercent,
  readReturnOutcome,
  sanitizePublicOnboarding,
} from "./publicPlatformConnectModel";

/**
 * Public platform connection — /connect/:onboardingToken
 *
 * A no-login page where one advertiser's client authorizes the platforms
 * Cphere will read. Modelled on the public Platform Analytics report and the
 * public funding page, and it keeps the same three properties:
 *
 *  1. SCOPE COMES FROM THE TOKEN. The URL segment is the entire credential.
 *     The page never sends an advertiser id, never sends an Authorization
 *     header, and never sends cookies. Which platforms may be authorized is
 *     decided on the server, from the invitation — not from this component.
 *
 *  2. EVERYTHING SHOWN IS SERVER COPY. Platform labels, access
 *     classifications, connection states, blockers and progress counts are
 *     rendered verbatim from the response. The page formats; it does not
 *     author a status. The response is reduced to the public contract's
 *     fields on arrival, so nothing outside that contract can reach the DOM.
 *
 *  3. NO APPLICATION CHROME. No navigation, no route into Cphere. The only
 *     outbound links are the platforms' own official authorization URLs,
 *     which the server builds and this page follows.
 *
 * Authorization happens entirely on the platform's own domain. Cphere never
 * sees a platform password, and no token, refresh token, authorization code
 * or PKCE verifier ever reaches this page — none of them are in the contract.
 */

const API_BASE_URL = (
  process.env.REACT_APP_BACKEND_URL || "https://cphere-backend.onrender.com"
).replace(/\/+$/, "");

const PHASES = Object.freeze({
  LOADING: "loading",
  UNAVAILABLE: "unavailable",
  LINK_CLOSED: "link_closed",
  READY: "ready",
});

/** No credentials, no headers. The token in the path is the whole request. */
const PUBLIC_FETCH = Object.freeze({ cache: "no-store", credentials: "omit" });

const PAGE_TITLE = "Platform Connect · Cphere";
// The same hero this surface's operator-side twin carries in Quantum:
// wordmark + capsule, "Platform" / "Connect". One product, one mark.
const HERO_TITLE = "PLATFORM CONNECT";
const HERO_WORDMARK = "PLATFORM";
const HERO_TAG = "CONNECT";

const INTRO_COPY =
  "Securely connect the platforms Cphere uses for campaign planning, activation, measurement and reporting. " +
  "You authorize each one directly with the platform, on the platform's own site. Cphere never sees your password.";

const ASSURANCE_COPY =
  "Every authorization happens on the platform's own domain. Cphere stores the access it is granted, encrypted, " +
  "and you can withdraw it from that platform at any time.";

const UNAVAILABLE_COPY = "This connection link is not available.";

const COMPLETE_TITLE = "PLATFORM CONNECTIONS COMPLETE";
const COMPLETE_BODY =
  "Cphere has everything it needs to begin reporting on this advertiser. Your Cphere team will confirm the first " +
  "data pull and let you know if anything else is needed. You can close this page.";

export default function PublicPlatformConnect() {
  const { onboardingToken } = useParams();

  const [phase, setPhase] = useState(PHASES.LOADING);
  const [page, setPage] = useState(null);
  const [busyPlatform, setBusyPlatform] = useState(null);
  const [platformError, setPlatformError] = useState(null);
  const [chooser, setChooser] = useState(null);
  const [returnOutcome, setReturnOutcome] = useState(null);
  const [announcement, setAnnouncement] = useState("");

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Document title + robots directive for the life of the page only.
  //
  // The API already answers with X-Robots-Tag, but a crawler that reaches
  // /connect/<token> is served this SPA shell, not the API — so the directive
  // has to exist in the document too, or an indexed token would be a live
  // onboarding link in a search result.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    let meta = null;
    try {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      meta.setAttribute("content", "noindex, nofollow");
      meta.setAttribute("data-public-platform-connect", "true");
      document.head.appendChild(meta);
    } catch {
      meta = null;
    }
    return () => {
      document.title = previousTitle;
      if (meta && meta.parentNode) meta.parentNode.removeChild(meta);
    };
  }, []);

  // The callback bridge appends only a platform id and a coarse outcome. It is
  // read once and then cleared from the address bar so a refresh does not
  // replay a stale banner.
  useEffect(() => {
    const outcome = readReturnOutcome(window.location.search);
    if (!outcome) return;
    setReturnOutcome(outcome);
    if (window.history?.replaceState) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const endpoint = useMemo(
    () => `${API_BASE_URL}/api/public/connect/${encodeURIComponent(onboardingToken || "")}`,
    [onboardingToken]
  );

  const load = useCallback(async () => {
    try {
      const response = await fetch(endpoint, PUBLIC_FETCH);
      if (!response.ok) {
        if (mountedRef.current) setPhase(PHASES.UNAVAILABLE);
        return;
      }
      const payload = await response.json();
      const next = sanitizePublicOnboarding(payload);
      if (!mountedRef.current) return;
      if (!next) {
        setPhase(PHASES.UNAVAILABLE);
        return;
      }
      setPage(next);
      setPhase(next.linkStatus === "expired" || next.linkStatus === "revoked" ? PHASES.LINK_CLOSED : PHASES.READY);
    } catch (_) {
      if (mountedRef.current) setPhase(PHASES.UNAVAILABLE);
    }
  }, [endpoint]);

  useEffect(() => {
    load();
  }, [load]);

  // Status is backend evidence, so the page re-reads it rather than assuming a
  // click moved anything. Polling pauses while a tab is hidden and while a
  // platform action is in flight.
  useEffect(() => {
    if (phase !== PHASES.READY) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "hidden" || busyPlatform) return;
      load();
    }, CONNECT_STATUS_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [phase, busyPlatform, load]);

  // A connection completed in another tab shows up here on the next focus.
  useEffect(() => {
    if (phase !== PHASES.READY) return undefined;
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [phase, load]);

  const startConnection = useCallback(
    async (card) => {
      setPlatformError(null);
      setBusyPlatform(card.id);
      setAnnouncement(`Starting ${card.label} authorization`);
      try {
        const response = await fetch(`${endpoint}/platforms/${encodeURIComponent(card.id)}/start`, {
          ...PUBLIC_FETCH,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.authorizeUrl) {
          if (!mountedRef.current) return;
          setBusyPlatform(null);
          setPlatformError({
            platformId: card.id,
            message: payload?.error || "Cphere could not start this connection. Please try again.",
          });
          return;
        }
        // Same tab, top-level navigation: the platform's own consent screen.
        window.location.assign(payload.authorizeUrl);
      } catch (_) {
        if (!mountedRef.current) return;
        setBusyPlatform(null);
        setPlatformError({
          platformId: card.id,
          message: "Cphere could not start this connection. Please check your connection and try again.",
        });
      }
    },
    [endpoint]
  );

  const openChooser = useCallback(
    async (card) => {
      setPlatformError(null);
      setBusyPlatform(card.id);
      setChooser({ platformId: card.id, loading: true, resources: [], message: null });
      try {
        const response = await fetch(
          `${endpoint}/platforms/${encodeURIComponent(card.id)}/accounts`,
          PUBLIC_FETCH
        );
        const payload = await response.json().catch(() => null);
        if (!mountedRef.current) return;
        setBusyPlatform(null);
        if (!response.ok) {
          setChooser(null);
          setPlatformError({
            platformId: card.id,
            message: "Cphere could not read your accounts from this platform.",
          });
          return;
        }
        setChooser({
          platformId: card.id,
          loading: false,
          resources: Array.isArray(payload?.resources) ? payload.resources : [],
          // The server's own reason when discovery is not possible. The page
          // never turns a discovery failure into "you have no accounts".
          message: payload?.ok === false ? payload.reason || null : null,
          resourceNoun: payload?.resourceNoun || card.accountResourceNoun || "account",
        });
      } catch (_) {
        if (!mountedRef.current) return;
        setBusyPlatform(null);
        setChooser(null);
        setPlatformError({
          platformId: card.id,
          message: "Cphere could not read your accounts from this platform.",
        });
      }
    },
    [endpoint]
  );

  const selectAccount = useCallback(
    async (card, resource, { confirmReplace = false } = {}) => {
      setPlatformError(null);
      setBusyPlatform(card.id);
      try {
        const response = await fetch(`${endpoint}/platforms/${encodeURIComponent(card.id)}/account`, {
          ...PUBLIC_FETCH,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ resourceId: resource.id, resourceName: resource.name, confirmReplace }),
        });
        const payload = await response.json().catch(() => null);
        if (!mountedRef.current) return;
        setBusyPlatform(null);

        if (response.status === 409 && payload?.code === "replacement_confirmation_required") {
          // Replacing an existing connection is an explicit decision.
          const proceed = window.confirm(payload.error);
          if (proceed) await selectAccount(card, resource, { confirmReplace: true });
          return;
        }
        if (!response.ok) {
          setPlatformError({
            platformId: card.id,
            message: payload?.error || "That account could not be connected.",
          });
          return;
        }
        setChooser(null);
        setAnnouncement(`${card.label} account connected`);
        await load();
      } catch (_) {
        if (!mountedRef.current) return;
        setBusyPlatform(null);
        setPlatformError({
          platformId: card.id,
          message: "That account could not be connected. Please try again.",
        });
      }
    },
    [endpoint, load]
  );

  if (phase === PHASES.LOADING) {
    return (
      <div className="prx-page ppc-page" data-public-platform-connect="loading">
        <div className="prx-unavailable" role="status" aria-busy="true">
          <p className="prx-unavailable-body">Loading your connection request…</p>
        </div>
      </div>
    );
  }

  if (phase === PHASES.UNAVAILABLE) {
    return (
      <div className="prx-page ppc-page" data-public-platform-connect="unavailable">
        <div className="prx-unavailable" role="status">
          <p className="prx-unavailable-title">{UNAVAILABLE_COPY}</p>
          <p className="prx-unavailable-body">
            If you were expecting to connect platforms for Cphere, ask your Cphere contact for a new link.
          </p>
        </div>
      </div>
    );
  }

  if (phase === PHASES.LINK_CLOSED) {
    return (
      <div
        className="prx-page ppc-page"
        data-public-platform-connect="link-closed"
        data-link-status={page.linkStatus}
      >
        <div className="prx-unavailable" role="status">
          <p className="prx-unavailable-title">{page.linkTitle || UNAVAILABLE_COPY}</p>
          {page.linkBody ? <p className="prx-unavailable-body">{page.linkBody}</p> : null}
        </div>
      </div>
    );
  }

  const { progress } = page;
  const connectedCards = page.platforms.filter((card) => card.healthy);
  const skippedCards = page.platforms.filter((card) => !card.healthy && !card.required);

  return (
    <div
      className="prx-page ppc-page"
      data-public-platform-connect="true"
      data-link-status={page.linkStatus}
      data-complete={progress.complete ? "true" : "false"}
    >
      <div className="cpx-page-container prx-page-container">
        <div className="cpx-page-content prx-page-content ppc-page-content">
          {/* Canonical brand mark, WITHOUT the authenticated onClick — there
              is no route from this page into the application. */}
          <CphereHeaderLogo className="cpx-brand-logo" />

          <header className="cphere-governed-hero-selector-stack prx-hero-stack">
            <div className="cpx-hero-container">
              <div className="cpx-hero-pill" role="heading" aria-level="1" aria-label={HERO_TITLE}>
                <span className="cpx-hero-pill-label cphere-hero-pill-wordmark">{HERO_WORDMARK}</span>
                <span className="cpx-hero-pill-tag cphere-hero-pill-tag">{HERO_TAG}</span>
              </div>
              <div className="cpx-hero-shimmer-bar" aria-hidden="true" />
            </div>

            <div className="prx-advertiser-stage ppc-identity">
              <p className="ppc-eyebrow">Platform access request</p>
              <AdvertiserIdentityMark
                name={page.advertiserName}
                logoUrl={page.advertiserLogoUrl}
                logoAlt={page.advertiserLogoAlt}
              />
              {page.campaignName ? (
                <p className="ppc-campaign-name">{page.campaignName}</p>
              ) : null}
              {page.requestedByName ? (
                <p className="ppc-requested-by">
                  Requested by {page.requestedByName}
                  {page.requestedByCompany ? ` · ${page.requestedByCompany}` : ""}
                </p>
              ) : (
                <p className="ppc-requested-by">Requested by {page.requestedByCompany}</p>
              )}
            </div>
          </header>

          <p className="ppc-visually-hidden" role="status" aria-live="polite">
            {announcement}
          </p>

          <main className="ppc-main">
            <section className="cpx-panel ppc-panel" aria-label="What Cphere is asking for">
              <p className="ppc-intro-copy">{INTRO_COPY}</p>
              {page.note ? <p className="ppc-note">{page.note}</p> : null}

              {returnOutcome ? (
                <div
                  className="ppc-banner"
                  role="status"
                  data-tone={returnOutcome.ok ? "positive" : "attention"}
                  data-return-outcome={returnOutcome.ok ? "success" : "error"}
                >
                  <p style={{ margin: 0 }}>
                    {returnOutcome.ok
                      ? "Authorization received. Cphere is verifying the connection — this card updates on its own."
                      : returnOutcome.copy}
                  </p>
                </div>
              ) : null}

              <div className="ppc-progress">
                <div className="ppc-progress-line">
                  <span className="ppc-progress-count">{formatProgressLine(progress)}</span>
                  {progress.outstandingRequired.length ? (
                    <span className="ppc-progress-outstanding">
                      {progress.outstandingRequired.length} still required
                    </span>
                  ) : null}
                </div>
                <div
                  className="ppc-progress-track"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={progress.totalCount}
                  aria-valuenow={progress.connectedCount}
                  aria-valuetext={formatProgressLine(progress)}
                >
                  <div className="ppc-progress-fill" style={{ width: `${progressPercent(progress)}%` }} />
                </div>
              </div>
            </section>

            <ul className="ppc-grid">
              {page.platforms.map((card) => (
                <PlatformCard
                  key={card.id}
                  card={card}
                  busy={busyPlatform === card.id}
                  error={platformError?.platformId === card.id ? platformError.message : null}
                  chooser={chooser?.platformId === card.id ? chooser : null}
                  onConnect={() => startConnection(card)}
                  onChoose={() => openChooser(card)}
                  onSelect={(resource) => selectAccount(card, resource)}
                  onCancelChooser={() => setChooser(null)}
                />
              ))}
            </ul>

            {progress.complete ? (
              <section className="cpx-panel ppc-panel ppc-complete" aria-label="Onboarding complete">
                <h2 className="ppc-complete-title">{COMPLETE_TITLE}</h2>
                <p className="ppc-complete-body">{COMPLETE_BODY}</p>

                <ul className="ppc-complete-list">
                  {connectedCards.map((card) => (
                    <li key={card.id} className="ppc-complete-chip" data-kind="connected">
                      <PlatformHeroMark heroKey={card.heroKey} label={card.label} size="xs" />
                      {card.label}
                    </li>
                  ))}
                  {skippedCards.map((card) => (
                    <li key={card.id} className="ppc-complete-chip" data-kind="skipped">
                      <PlatformHeroMark heroKey={card.heroKey} label={card.label} size="xs" />
                      {card.label} · not connected
                    </li>
                  ))}
                </ul>

                <p className="ppc-complete-meta">
                  {page.advertiserName ? `${page.advertiserName} · ` : ""}
                  {page.campaignName ? `${page.campaignName} · ` : ""}
                  {page.completedAt ? `Completed ${formatTimestamp(page.completedAt)}` : null}
                </p>
              </section>
            ) : null}

            <p className="ppc-assurance">{ASSURANCE_COPY}</p>
          </main>

          <CphereBrandFooter
            className="prx-footer ppc-footer"
            trail=""
            textClassName="prx-footer-credit"
            note={
              page.expiresAt ? (
                <p className="prx-footer-note ppc-footer-note">
                  Link expires {formatTimestamp(page.expiresAt)}
                </p>
              ) : null
            }
          />
        </div>
      </div>
    </div>
  );
}

/**
 * One platform. The card renders the server's state verbatim and offers an
 * action only where the server says one can succeed — a platform that is
 * disabled by policy or awaiting provider setup gets the real explanation
 * instead of a button that would have to refuse.
 */
function PlatformCard({ card, busy, error, chooser, onConnect, onChoose, onSelect, onCancelChooser }) {
  const action = connectActionFor(card);
  const heroSrc = resolvePlatformHeroImage(card.heroKey);

  return (
    <li className="ppc-card" data-platform={card.id} data-state={card.state} data-tone={card.tone}>
      <div className="ppc-card-hero">
        <img
          className="ppc-card-hero-image"
          src={heroSrc}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
        />
        <div className="ppc-card-hero-content">
          {/* The bounded mark sits beside the name at every size, so the
              platform is identifiable even where the full-bleed art behind it
              is dark or crops badly. */}
          <PlatformHeroMark heroKey={card.heroKey} label={card.label} size="md" />
          <h3 className="ppc-card-name">
            {card.label}
            {card.officialName && card.officialName !== card.label ? (
              <span className="ppc-card-official">{card.officialName}</span>
            ) : null}
          </h3>
          {card.required ? <span className="ppc-card-required">Required</span> : null}
        </div>
      </div>

      <div className="ppc-card-body">
        {card.accessClassLabel ? (
          <span className="ppc-card-access">{card.accessClassLabel}</span>
        ) : null}

        {card.enables ? <p className="ppc-card-enables">{card.enables}</p> : null}
        {card.purpose ? <p className="ppc-card-purpose">{card.purpose}</p> : null}
        {card.scopeSummary ? <p className="ppc-card-scope">{card.scopeSummary}</p> : null}

        <div className="ppc-card-status">
          <span className="ppc-status-dot" aria-hidden="true" />
          <span className="ppc-status-label">{card.stateLabel}</span>
        </div>

        {/* The line box is reserved whether or not there is anything to say,
            so a state change never moves the button below it. */}
        <p className="ppc-card-blocker" data-empty={error || card.blocker ? "false" : "true"}>
          {error || card.blocker || " "}
        </p>

        {card.selectedAccount ? (
          <p className="ppc-card-account">
            Using {card.selectedAccount.name || card.selectedAccount.id}
            {card.selectedAccount.name && card.selectedAccount.id ? (
              <span className="ppc-card-account-id"> · {card.selectedAccount.id}</span>
            ) : null}
          </p>
        ) : null}

        {chooser ? (
          <div className="ppc-chooser">
            <p className="ppc-label">Choose {chooser.resourceNoun || "an account"}</p>
            {chooser.loading ? (
              <p className="ppc-chooser-message">Reading your accounts…</p>
            ) : chooser.message ? (
              <p className="ppc-chooser-message">{chooser.message}</p>
            ) : chooser.resources.length ? (
              <ul className="ppc-chooser-list">
                {chooser.resources.map((resource) => (
                  <li key={resource.id}>
                    <button
                      type="button"
                      className="ppc-chooser-option"
                      aria-pressed={resource.alreadySelected ? "true" : "false"}
                      disabled={busy}
                      onClick={() => onSelect(resource)}
                    >
                      <span>{resource.name}</span>
                      {resource.detail ? (
                        <span className="ppc-chooser-option-id">{resource.detail}</span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="ppc-chooser-message">
                Cphere could not find an account this authorization can use. Check that you signed in with the
                profile that has access, then authorize again.
              </p>
            )}
            <div className="ppc-card-actions">
              <button type="button" className="ppc-button ppc-button-quiet" onClick={onCancelChooser}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="ppc-card-actions">
            {action ? (
              <button
                type="button"
                className={`ppc-button${action.kind === "reconnect" ? " ppc-button-quiet" : ""}`}
                disabled={busy}
                onClick={action.kind === "choose" ? onChoose : onConnect}
              >
                {busy ? "Working…" : action.label}
              </button>
            ) : null}
            {action?.kind !== "choose" && card.accountSelectionRequired && card.facts.oauthAuthorized ? (
              <button type="button" className="ppc-button ppc-button-quiet" disabled={busy} onClick={onChoose}>
                Change {card.accountResourceNoun || "account"}
              </button>
            ) : null}
          </div>
        )}

        <details className="ppc-facts">
          <summary className="ppc-facts-summary">Connection detail</summary>
          <dl className="ppc-facts-list">
            {factRows(card).map((row) => (
              <div className="ppc-fact-row" key={row.key}>
                <dt>{row.label}</dt>
                <dd className="ppc-fact-value">{row.value}</dd>
              </div>
            ))}
          </dl>
        </details>
      </div>
    </li>
  );
}
